import React from 'react'
import {
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  useTheme,
  alpha,
} from '@mui/material'
import { TrackerState } from '../../types/gotypes'

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
    {children}
  </Typography>
)

// ReferenceView is the landing tab: the vision, a development summary of the
// tracker's sections, the leadership structure, and a feed of programme
// updates (the change log, newest first).
export const ReferenceView: React.FC<{ tracker: TrackerState }> = ({ tracker }) => {
  const theme = useTheme()
  const { reference, branding, sections } = tracker
  const clientLabel = branding.customerName
  const primary = branding.primaryColor || theme.palette.primary.main

  return (
    <Stack spacing={4}>
      {reference.vision && (
        <Box>
          <SectionTitle>The Vision</SectionTitle>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderLeft: `3px solid ${primary}`,
              bgcolor: alpha(primary, 0.04),
            }}
          >
            <Typography sx={{ fontSize: 16, lineHeight: 1.6, color: theme.palette.text.primary }}>
              {reference.vision}
            </Typography>
          </Paper>
        </Box>
      )}

      {sections.length > 0 && (
        <Box>
          <SectionTitle>Development Summary</SectionTitle>
          <Stack spacing={1.5}>
            {sections.map((section) => (
              <Paper key={section.id} elevation={0} sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 0.5 }}>{section.name}</Typography>
                {section.description && (
                  <Typography sx={{ fontSize: 14, color: theme.palette.text.secondary, lineHeight: 1.55 }}>
                    {section.description}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      <Box>
        <SectionTitle>Leadership structure</SectionTitle>
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.03) }}>
                <TableCell sx={{ fontWeight: 700 }}>Stream</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Bayes Price</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{clientLabel}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reference.leadership?.map((row, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ color: theme.palette.text.secondary }}>{row.stream}</TableCell>
                  <TableCell>{row.role}</TableCell>
                  <TableCell>{row.bp}</TableCell>
                  <TableCell>{row.client}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {reference.changeLog && reference.changeLog.length > 0 && (
        <Box>
          <SectionTitle>Programme updates</SectionTitle>
          <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
            {reference.changeLog.map((entry, i) => (
              <Box key={i}>
                {i > 0 && <Divider />}
                <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: theme.palette.text.secondary, minWidth: 80 }}>
                    {entry.date}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {entry.item && (
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: theme.palette.text.secondary }}>
                        {entry.item}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 13.5, color: theme.palette.text.primary }}>{entry.change}</Typography>
                    {entry.author && (
                      <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled, mt: 0.25 }}>{entry.author}</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Paper>
        </Box>
      )}
    </Stack>
  )
}

export default ReferenceView
